import React, { useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Row, Select, Space, Statistic, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys, PAYROLL_RUN_SCOPE_LABEL } from '@/services/hrms.service';
import { useHrmsPolicy } from '@/hooks/useHrmsPolicy';
import { PAYROLL_STATUS_COLOR, hrmsStatusColor } from './shared/hrmsStatus';

const { Text } = Typography;

const PayrollPage: React.FC = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>();
  const { indiaPack } = useHrmsPolicy();

  const { data: periods = [], isLoading } = useQuery({
    queryKey: hrmsKeys.payrollPeriods(),
    queryFn: () => hrmsService.listPayrollPeriods(),
  });

  const filtered = useMemo(
    () => (statusFilter ? periods.filter((r: any) => r.status === statusFilter) : periods),
    [periods, statusFilter]
  );

  const stats = useMemo(() => {
    const open = periods.filter((r: any) => !['Locked', 'Paid'].includes(r.status)).length;
    const locked = periods.filter((r: any) => r.status === 'Locked' || r.status === 'Paid').length;
    const net = periods.reduce((s: number, r: any) => s + Number(r.total_net || 0), 0);
    return { total: periods.length, open, locked, net };
  }, [periods]);

  const columns: ColumnsType<any> = [
    {
      title: 'Period',
      key: 'p',
      width: 120,
      fixed: 'left',
      render: (_, r) => (
        <a onClick={() => navigate(`/hr/payroll/periods/${r.id}`)}>
          {r.period_year
            ? `${String(r.period_month).padStart(2, '0')}/${r.period_year}`
            : r.period_name  || 'Open'}
        </a>
      ),
    },
    {
      title: 'Run',
      key: 'run',
      width: 220,
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text>{r.period_number || `Run ${r.run_number}`}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {PAYROLL_RUN_SCOPE_LABEL[r.run_scope as keyof typeof PAYROLL_RUN_SCOPE_LABEL] || r.run_scope}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      render: (s: string) => <Tag color={hrmsStatusColor(s, PAYROLL_STATUS_COLOR)}>{s}</Tag>,
    },
    { title: 'Employees', dataIndex: 'employee_count', width: 100 },
    { title: 'Gross', dataIndex: 'total_gross', width: 110 },
    { title: 'Net', dataIndex: 'total_net', width: 110 },
    {
      title: 'Actions',
      key: 'a',
      fixed: 'right',
      width: 90,
      render: (_, r) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/hr/payroll/periods/${r.id}`)}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Payroll periods"
          subtitle={
            indiaPack
              ? 'Open periods, compute, lock, pay, and export NEFT'
              : 'Open periods, compute, lock, pay, and export WPS'
          }
          primaryActions={
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/hr/payroll/periods/new')}>
              Open period
            </Button>
          }
        />
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Payroll cycle"
        description={
          <span>
            Open period → Compute (salary heads, approved OT, monthly adjustments, unpaid leave, loans
            {indiaPack ? ', EPF/ESI/PT/TDS' : ''}) → Submit for approval → Approve → Lock → Finance JV, payment batch
            &amp; {indiaPack ? 'NEFT' : 'WPS'} export. Map salary GL accounts on{' '}
            <Link to="/hr/setup">HRMS Setup</Link> before the Finance JV.
          </span>
        }
      />
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}><Card size="small"><Statistic title="Periods" value={stats.total} /></Card></Col>
        <Col xs={12} sm={6}><Card size="small"><Statistic title="In progress" value={stats.open} /></Card></Col>
        <Col xs={12} sm={6}><Card size="small"><Statistic title="Locked / Paid" value={stats.locked} /></Card></Col>
        <Col xs={12} sm={6}><Card size="small"><Statistic title="Total net" value={stats.net} precision={2} /></Card></Col>
      </Row>
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space>
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 160 }}
            value={statusFilter}
            onChange={setStatusFilter}
            options={Object.keys(PAYROLL_STATUS_COLOR).map((v) => ({ value: v, label: v }))}
          />
          <Text type="secondary">{filtered.length} period(s)</Text>
        </Space>
      </Card>
      <Table
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={filtered}
        pagination={{ pageSize: 20 }}
        onRow={(r) => ({
          onClick: () => navigate(`/hr/payroll/periods/${r.id}`),
          style: { cursor: 'pointer' },
        })}
      />
    </WorkspaceLayout>
  );
};

export default PayrollPage;
